/**
 * Nosto Tracking Utilities
 * Handles api.recordSearch() calls with proper data structures
 */

// Track which products have already been viewed to prevent duplicates
// Use sessionStorage to persist across component re-mounts
const getViewedProducts = () => {
    if (typeof window === 'undefined') return new Set();
    try {
        const stored = sessionStorage.getItem('nosto_viewed_products');
        return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
        return new Set();
    }
};

const setViewedProducts = (viewedProducts) => {
    if (typeof window === 'undefined') return;
    try {
        sessionStorage.setItem('nosto_viewed_products', JSON.stringify([...viewedProducts]));
    } catch {
        // Fallback to in-memory only
    }
};

/**
 * Clear the tracking cache (useful for testing or page refreshes)
 */
export const clearTrackingCache = () => {
    if (typeof window !== 'undefined') {
        try {
            sessionStorage.removeItem('nosto_viewed_products');
        } catch {
            // Ignore errors
        }
    }
}

/**
 * Record View Category event with deduplication
 * @param {string} categoryTitle - Category title/name
 * @param {string} categoryId - Category ID
 * @param {string} ref - Reference/placement
 * @param {string} type - Event type (default: 'vc')
 */
export const recordViewCategory = (categoryTitle, categoryId, ref, type = 'vc') => {
    if (typeof window === 'undefined' || !window.nostojs) {
        return
    }

    // Create a unique key for this category + ref combination
    const eventKey = `${categoryTitle}-${categoryId}-${ref}-${type}`

    // Get current viewed products from persistent storage
    const viewedProducts = getViewedProducts();

    // Check if we've already tracked this event
    if (viewedProducts.has(eventKey)) {
        return
    }

    // Mark this event as tracked
    viewedProducts.add(eventKey)
    setViewedProducts(viewedProducts);

    try {
        window.nostojs((api) => {
            // Build the session chain with category ID if provided
            let session = api.defaultSession()
                .viewCategory(categoryTitle)

            // Add category ID if provided (as per Nosto docs)
            if (categoryId) {
                session = session.setCategoryIds([categoryId])
            }

            // Add ref (slotID) if provided - required for proper event attribution
            if (ref) {
                session = session.setRef(ref)
            }

            session.load()
                .then((data) => {
                    console.log(`✅ Nosto View Category recorded for ${categoryTitle} (ID: ${categoryId}, ref: ${ref})`)
                })
                .catch((error) => {
                    console.error(`❌ Nosto View Category event error:`, error)
                    // Remove from set if tracking failed so we can retry
                    const viewedProducts = getViewedProducts();
                    viewedProducts.delete(eventKey)
                    setViewedProducts(viewedProducts);
                })
        })
    } catch (error) {
        console.error('❌ Error calling Nosto viewCategory for View Category:', error)
        // Remove from set if tracking failed so we can retry
        const viewedProducts = getViewedProducts();
        viewedProducts.delete(eventKey)
        setViewedProducts(viewedProducts);
    }
}

/**
 * Record Add to Cart event with deduplication
 * @param {string} productId - Product ID
 * @param {string} ref - Reference/placement
 * @param {string} type - Event type (default: 'cp')
 */
export const recordAddToCart = (productId, ref, type = 'cp') => {
    if (typeof window === 'undefined' || !window.nostojs) {
        return
    }

    try {
        window.nostojs((api) => {
            api.reportAddToCart({
                productId: productId,
                ref: ref
            });
            console.log('✅ Nosto reportAddToCart called:', { productId, ref });
        })
    } catch (error) {
        console.error('❌ Error calling Nosto reportAddToCart:', error)
    }
}

/**
 * Record a View Product event with deduplication
 * @param {string} productId - Product ID
 * @param {string} ref - Reference/placement
 * @param {string} type - Event type (default: 'vp')
 */
export const recordViewProduct = (productId, ref, type = 'vp') => {
    if (typeof window === 'undefined' || !window.nostojs) {
        return
    }

    // Create a unique key for this product + ref combination
    const eventKey = `${productId}-${ref}-${type}`

    // Get current viewed products from persistent storage
    const viewedProducts = getViewedProducts();

        // Check if we've already tracked this event
    if (viewedProducts.has(eventKey)) {
        return
    }

    // Mark this event as tracked
    viewedProducts.add(eventKey)
    setViewedProducts(viewedProducts);

    try {
        window.nostojs((api) => {
            let session = api.defaultSession()
                .viewProduct(productId)

            // Add ref (slotID/placement) if provided - required for proper event attribution
            if (ref) {
                session = session.setRef(ref)
            }

            session.load()
                .then((data) => {
                    // Success - no logging needed
                })
                .catch((error) => {
                    console.error(`❌ Nosto View Product event error:`, error)
                    // Remove from set if tracking failed so we can retry
                    const viewedProducts = getViewedProducts();
                    viewedProducts.delete(eventKey)
                    setViewedProducts(viewedProducts);
                })
        })
    } catch (error) {
        console.error('❌ Error calling Nosto defaultSession viewProduct:', error)
        // Remove from set if tracking failed so we can retry
        const viewedProducts = getViewedProducts();
        viewedProducts.delete(eventKey)
        setViewedProducts(viewedProducts);
    }
}

/**
 * Record search submit (serp) analytics
 * @param {string} query - Search query
 * @param {Array} products - Array of product objects
 */
export const recordSearchSubmit = (query, products) => {
    if (typeof window === 'undefined' || !window.nostojs) {
        return
    }

    // Add debugging to understand the data structure
    console.log('🔍 recordSearchSubmit called with:', { query, productsCount: products?.length, sampleProduct: products?.[0] })

    // Validate that products have the required structure
    if (!Array.isArray(products) || products.length === 0) {
        console.warn('❌ recordSearchSubmit: No valid products provided')
        return
    }

    try {
        window.nostojs((api) => {
            // Create the search request object as expected by the API
            const searchRequest = {
                query: query,
                products: {
                    fields: ['name', 'productId', 'url', 'imageUrl', 'price', 'listPrice', 'brand'],
                    size: products.length
                }
            }

            // Create the search result object as expected by the API
            const searchResult = {
                products: {
                    hits: products.map(product => ({
                        productId: product.productId || product.id || '',
                        name: product.name || product.productName || '',
                        url: product.url || '',
                        imageUrl: product.imageUrl || product.image || '',
                        price: product.price || 0,
                        listPrice: product.listPrice || product.compareAtPrice || 0,
                        brand: product.brand || 'Monica + Andy'
                    })),
                    total: products.length,
                    size: products.length,
                    from: 0
                }
            }

            console.log('🔍 Calling api.recordSearch with correct format:', {
                type: 'serp',
                searchRequest,
                searchResult
            })

            // Use the correct API format according to Nosto docs
            const result = api.recordSearch(
                'serp',           // type
                searchRequest,    // request
                searchResult      // result
            )

            // Check if the method returned a promise-like object
            if (result && typeof result.done === 'function') {
                result
                    .done()
                    .then((response) => {
                        console.log('✅ Nosto search submit analytics recorded successfully:', response)
                    })
                    .catch((error) => {
                        console.error(`❌ Nosto search submit analytics error:`, error)
                    })
            } else {
                console.log('✅ Nosto search submit analytics recorded successfully (no promise returned)')
            }
        })
    } catch (error) {
        console.error('❌ Error calling Nosto recordSearch for submit:', error)
    }
}

// Store for autocomplete event deduplication
let lastAutocompleteQuery = '';
let lastAutocompleteTimestamp = 0;

/**
 * Record search input/autocomplete analytics with deduplication
 * @param {string} query - Search query
 * @param {Array} products - Array of product objects
 */
export const recordSearchAutocomplete = (query, products) => {
    if (typeof window === 'undefined' || !window.nostojs) {
        return
    }

    // Deduplicate identical queries within 500ms
    const now = Date.now();
    if (query === lastAutocompleteQuery && (now - lastAutocompleteTimestamp) < 500) {
        console.log('🔍 Skipping duplicate autocomplete event for query:', query);
        return;
    }

    lastAutocompleteQuery = query;
    lastAutocompleteTimestamp = now;

    // Add debugging to understand the data structure
    console.log('🔍 recordSearchAutocomplete called with:', { query, productsCount: products?.length, sampleProduct: products?.[0] })

    // Validate that products have the required structure
    if (!Array.isArray(products) || products.length === 0) {
        console.warn('❌ recordSearchAutocomplete: No valid products provided')
        return
    }

    try {
        window.nostojs((api) => {
            // Create the search request object as expected by the API
            const searchRequest = {
                query: query,
                products: {
                    fields: ['name', 'productId', 'url', 'imageUrl', 'price', 'listPrice', 'brand'],
                    size: products.length
                }
            }

            // Create the search result object as expected by the API
            const searchResult = {
                products: {
                    hits: products.map(product => ({
                        productId: product.productId || product.id || '',
                        name: product.name || product.productName || '',
                        url: product.url || '',
                        imageUrl: product.imageUrl || product.image || '',
                        price: product.price || 0,
                        listPrice: product.listPrice || product.compareAtPrice || 0,
                        brand: product.brand || 'Monica + Andy'
                    })),
                    total: products.length,
                    size: products.length,
                    from: 0
                }
            }

            console.log('🔍 Calling api.recordSearch with correct format:', {
                type: 'autocomplete',
                searchRequest,
                searchResult
            })

            // Use the correct API format according to Nosto docs
            const result = api.recordSearch(
                'autocomplete',  // type
                searchRequest,    // request
                searchResult,     // result
                { isKeyword: false }  // options
            )

            // Check if the method returned a promise-like object
            if (result && typeof result.done === 'function') {
                result
                    .done()
                    .then((response) => {
                        console.log('✅ Nosto autocomplete analytics recorded successfully')
                    })
                    .catch((error) => {
                        console.error(`❌ Nosto autocomplete analytics error:`, error)
                    })
            } else {
                console.warn('⚠️ api.recordSearch did not return a promise-like object:', result)
                // Try alternative approach - just call the method without chaining
                console.log('✅ Nosto autocomplete analytics called (no promise returned)')
            }
        })
    } catch (error) {
        console.error('❌ Error calling Nosto recordSearch for autocomplete:', error)
    }
}

/**
 * Record category/collection search analytics
 * @param {string} categoryId - Category/collection ID
 * @param {Array} products - Array of product objects
 * @param {Object} filters - Applied filters (optional)
 */
export const recordCategorySearch = (categoryId, products, filters = {}) => {
    if (typeof window === 'undefined' || !window.nostojs) {
        return
    }

    // Validate that products have the required structure
    if (!Array.isArray(products) || products.length === 0) {
        console.warn('❌ recordCategorySearch: No valid products provided')
        return
    }

    try {
        window.nostojs((api) => {
            console.log(`🔍 Recording Nosto category analytics:`, { categoryId, productsCount: products.length, filters })

            // Create the search request object as expected by the API
            const searchRequest = {
                query: categoryId,
                products: {
                    fields: ['name', 'productId', 'url', 'imageUrl', 'price', 'listPrice', 'brand'],
                    size: products.length,
                    ...filters
                }
            }

            // Create the search result object as expected by the API
            const searchResult = {
                products: {
                    hits: products.map(product => ({
                        productId: product.productId || product.id || '',
                        name: product.name || product.productName || '',
                        url: product.url || '',
                        imageUrl: product.imageUrl || product.image || '',
                        price: product.price || 0,
                        listPrice: product.listPrice || product.compareAtPrice || 0,
                        brand: product.brand || 'Monica + Andy'
                    })),
                    total: products.length,
                    size: products.length,
                    from: 0
                }
            }

            // Use the correct API format with proper category metadata
            const result = api.recordSearch(
                'category',       // type
                searchRequest,    // request
                searchResult,     // result
                {                 // category metadata
                    categoryId: categoryId,
                    categoryName: categoryId
                }
            )

            // Check if the method returned a promise-like object
            if (result && typeof result.done === 'function') {
                result
                    .done()
                    .then((response) => {
                        console.log(`✅ Nosto category analytics recorded successfully:`, response)
                    })
                    .catch((error) => {
                        console.error(`❌ Nosto category analytics error:`, error)
                    })
            } else {
                console.log(`✅ Nosto category analytics recorded successfully (no promise returned)`)
            }
        })
    } catch (error) {
        console.error('❌ Error calling Nosto recordSearch for category:', error)
    }
}

/**
 * Record category search with path (for collection pages)
 * @param {string} categoryPath - Category path
 * @param {Array} products - Array of product objects
 * @param {Object} filters - Applied filters (optional)
 */
export const recordCategorySearchByPath = (categoryPath, products, filters = {}) => {
    if (typeof window === 'undefined' || !window.nostojs) {
        return
    }

    // Validate that products have the required structure
    if (!Array.isArray(products) || products.length === 0) {
        console.warn('❌ recordCategorySearchByPath: No valid products provided')
        return
    }

    try {
        window.nostojs((api) => {
            console.log(`🔍 Recording Nosto category path analytics:`, { categoryPath, productsCount: products.length, filters })

            // Create the search request object as expected by the API
            const searchRequest = {
                query: categoryPath,
                products: {
                    fields: ['name', 'productId', 'url', 'imageUrl', 'price', 'listPrice', 'brand'],
                    size: products.length,
                    ...filters
                }
            }

            // Create the search result object as expected by the API
            const searchResult = {
                products: {
                    hits: products.map(product => ({
                        productId: product.productId || product.id || '',
                        name: product.name || product.productName || '',
                        url: product.url || '',
                        imageUrl: product.imageUrl || product.image || '',
                        price: product.price || 0,
                        listPrice: product.listPrice || product.compareAtPrice || 0,
                        brand: product.brand || 'Monica + Andy'
                    })),
                    total: products.length,
                    size: products.length,
                    from: 0
                }
            }

            // Use the correct API format with proper category metadata
            const result = api.recordSearch(
                'category',       // type
                searchRequest,    // request
                searchResult,     // result
                {                 // category metadata
                    categoryId: categoryPath,
                    categoryName: categoryPath
                }
            )

            // Check if the method returned a promise-like object
            if (result && typeof result.done === 'function') {
                result
                    .done()
                    .then((response) => {
                        console.log(`✅ Nosto category path analytics recorded successfully:`, response)
                    })
                    .catch((error) => {
                        console.error(`❌ Nosto category path analytics error:`, error)
                    })
            } else {
                console.log(`✅ Nosto category path analytics recorded successfully (no promise returned)`)
            }
        })
    } catch (error) {
        console.error('❌ Error calling Nosto recordSearch for category path:', error)
    }
}

/**
 * Record search analytics for SERP, autocomplete, or category pages
 * @param {string} type - Search type: 'serp', 'autocomplete', 'category'
 * @param {Object} searchRequest - Full search API request
 * @param {Object} searchResult - Full search API result
 * @param {Object} options - Optional analytics options and session data
 * @param {boolean} options.isKeyword - Whether this is a keyword search (for autocomplete)
 * @param {Object} options.sessionParams - Nosto session parameters (segments, affinities)
 * @param {Array} options.segments - User segments for A/B testing
 * @param {Array} options.abTests - Active A/B test assignments
 */
export const recordSearch = (type, searchRequest, searchResult, options = {}) => {
    if (typeof window === 'undefined' || !window.nostojs) {
        console.log('🔍 recordSearch: window.nostojs not available')
        return
    }

    try {
        window.nostojs((api) => {
            // Enrich searchRequest with session data for proper A/B test attribution
            const enrichedSearchRequest = { ...searchRequest }

            // Add sessionParams if provided (includes segments and user affinities)
            if (options.sessionParams) {
                enrichedSearchRequest.sessionParams = options.sessionParams
            }

            // Add segments if provided (for A/B test targeting)
            if (options.segments && Array.isArray(options.segments) && options.segments.length > 0) {
                enrichedSearchRequest.segments = options.segments
            }

            // Add abTests if provided (for A/B test attribution)
            if (options.abTests && Array.isArray(options.abTests) && options.abTests.length > 0) {
                enrichedSearchRequest.abTests = options.abTests
            }

            console.log('🔍 Nosto API - recordSearch called with:', {
                type,
                searchRequest: enrichedSearchRequest,
                searchResult,
                hasSessionParams: !!options.sessionParams,
                hasSegments: !!(options.segments?.length),
                hasAbTests: !!(options.abTests?.length),
                timestamp: new Date().toISOString(),
                nostojsAvailable: !!window.nostojs
            })

            try {
                // Use the format from Nosto docs: api.recordSearch(type, searchRequest, searchResult)
                // Note: This method doesn't return a value - it's a fire-and-forget tracking call
                // Pass enriched request with session data for proper attribution
                api.recordSearch(type, enrichedSearchRequest, searchResult)
                console.log('✅ Nosto API - recordSearch tracking sent successfully for type:', type)
                console.log('📊 Nosto API - recordSearch data sent:', {
                    type,
                    requestSize: enrichedSearchRequest?.products?.size || 0,
                    resultSize: searchResult?.products?.hits?.length || 0,
                    withSessionParams: !!options.sessionParams,
                    withSegments: !!(options.segments?.length),
                    withAbTests: !!(options.abTests?.length)
                })
            } catch (apiError) {
                console.error('❌ Nosto API - recordSearch error:', apiError)
            }
        })
    } catch (error) {
        console.error('❌ Error calling Nosto recordSearch:', error)
    }
}

/**
 * Record search product click analytics
 * @param {string} type - Search type: 'serp', 'autocomplete', 'category'
 * @param {Object} hit - Product object containing productId and url
 */
export const recordSearchClick = (type, hit) => {
    if (typeof window === 'undefined' || !window.nostojs) {
        console.log('🔍 recordSearchClick: window.nostojs not available')
        return
    }

    try {
        window.nostojs((api) => {
            console.log('🔍 Nosto API - recordSearchClick called with:', {
                type,
                hit,
                timestamp: new Date().toISOString(),
                nostojsAvailable: !!window.nostojs
            })

            try {
                // Use the format from Nosto docs: api.recordSearchClick(type, hit)
                const response = api.recordSearchClick(type, hit)
                console.log('✅ Nosto API - recordSearchClick successful, response:', response)
            } catch (apiError) {
                console.error('❌ Nosto API - recordSearchClick error:', apiError)
            }
        })
    } catch (error) {
        console.error('❌ Error calling Nosto recordSearchClick:', error)
    }
}

/**
 * Record search add to cart analytics
 * @param {string} type - Search type: 'serp', 'autocomplete', 'category'
 * @param {Object} hit - Product object containing productId and url
 */
export const recordSearchAddToCart = (type, hit) => {
    if (typeof window === 'undefined' || !window.nostojs) {
        console.log('🔍 recordSearchAddToCart: window.nostojs not available')
        return
    }

    try {
        window.nostojs((api) => {
            console.log('🛒 Nosto API - recordSearchAddToCart called with:', {
                type,
                hit,
                timestamp: new Date().toISOString(),
                nostojsAvailable: !!window.nostojs
            })

            try {
                const response = api.recordSearchAddToCart(type, {
                    productId: hit.productId,
                    url: hit.url
                })
                console.log('✅ Nosto API - recordSearchAddToCart successful, response:', response)
            } catch (apiError) {
                console.error('❌ Nosto API - recordSearchAddToCart error:', apiError)
            }
        })
    } catch (error) {
        console.error('❌ Error calling Nosto recordSearchAddToCart:', error)
    }
}
